import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Clock,
  EyeOff,
  Hourglass,
  WifiOff,
} from "@tamagui/lucide-icons";
import { SensorAlertType } from "common";
import { PieChart } from "react-gifted-charts";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  Text,
  XStack,
  YStack,
  View,
  getTokenValue,
  styled,
  Token,
} from "tamagui";

import { BorderedCard } from "./BorderedCard";
import { KPICard } from "./KPICard";
import {
  FleetAnalytics,
  OVERDUE_SERVICE_REQUEST_DAYS,
  SILENT_SENSOR_HOURS,
  STALE_ALERT_DAYS,
} from "../hooks/useAdminAnalytics";
import { useSensorType } from "../hooks/useSensorType";

interface CountProps {
  count: number;
}

// Four / three to a row on a full desktop, two below `lg` (a 190px tile
// ellipsizes its label to a few letters), one on a phone.
const KPISlot = styled(View, {
  flexGrow: 1,
  flexBasis: 0,
  $lg: { flexBasis: "40%" },
  $xs: { flexBasis: "100%" },
});

export const OfflineHubsCard = ({ count }: CountProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <KPISlot>
      <KPICard
        error
        icon={WifiOff}
        title={count}
        description={t("analytics.offlineHubs")}
        onPress={() => navigate("/base-stations?status=INACTIVE")}
      />
    </KPISlot>
  );
};

export const SensorErrorsCard = ({ count }: CountProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <KPISlot>
      <KPICard
        error
        icon={AlertCircle}
        title={count}
        description={t("analytics.sensorErrors")}
        onPress={() =>
          navigate(`/alerts?tab=active&type=${SensorAlertType.SensorInactive}`)
        }
      />
    </KPISlot>
  );
};

export const SensorAlertsCard = ({ count }: CountProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <KPISlot>
      <KPICard
        warning
        icon={Bell}
        title={count}
        description={t("analytics.sensorAlerts")}
        onPress={() => navigate("/alerts?tab=active")}
      />
    </KPISlot>
  );
};

export const ActiveSensorsCard = ({ count }: CountProps) => {
  const { t } = useTranslation();
  return (
    <KPISlot>
      <KPICard
        success
        icon={CheckCircle2}
        title={count}
        description={t("analytics.activeSensors")}
      />
    </KPISlot>
  );
};

export const StaleAlertsCard = ({ count }: CountProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <KPISlot>
      <KPICard
        warning
        icon={Clock}
        title={count}
        description={t("analytics.staleAlerts", { days: STALE_ALERT_DAYS })}
        onPress={() => navigate(`/alerts?tab=active&age=${STALE_ALERT_DAYS}d`)}
      />
    </KPISlot>
  );
};

// No destination: a non-zero value is a bug in the offline detector, not a
// list an operator works through.
export const SilentSensorsCard = ({ count }: CountProps) => {
  const { t } = useTranslation();
  return (
    <KPISlot>
      <KPICard
        error
        icon={EyeOff}
        title={count}
        description={t("analytics.silentSensors", {
          hours: SILENT_SENSOR_HOURS,
        })}
      />
    </KPISlot>
  );
};

export const OverdueServiceRequestsCard = ({ count }: CountProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <KPISlot>
      <KPICard
        warning
        icon={Hourglass}
        title={count}
        description={t("analytics.overdueServiceRequests", {
          days: OVERDUE_SERVICE_REQUEST_DAYS,
        })}
        onPress={() =>
          navigate(
            `/service-requests?status=All&age=${OVERDUE_SERVICE_REQUEST_DAYS}d`,
          )
        }
      />
    </KPISlot>
  );
};

const HealthBar = ({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) => (
  <>
    <XStack justifyContent="space-between" alignItems="center">
      <Text fontSize="$3">{label}</Text>
      <Text fontSize="$3">{count}</Text>
    </XStack>
    <View height={8} backgroundColor="$gray5" borderRadius="$2">
      <View
        height={8}
        backgroundColor={color}
        borderRadius="$2"
        width={`${total ? (count / total) * 100 : 0}%`}
      />
    </View>
  </>
);

export const SensorHealthCard = ({
  activeSensorCount,
  alertingSensorCount,
  erroringSensorCount,
  onlineHubCount,
  offlineHubCount,
  sensorsBehindOfflineHubs,
}: Pick<
  FleetAnalytics,
  | "activeSensorCount"
  | "alertingSensorCount"
  | "erroringSensorCount"
  | "onlineHubCount"
  | "offlineHubCount"
  | "sensorsBehindOfflineHubs"
>) => {
  const { t } = useTranslation();
  const total = activeSensorCount + alertingSensorCount + erroringSensorCount;

  return (
    <BorderedCard title={t("analytics.sensorHealth")}>
      <YStack gap="$3">
        <Text fontSize={24}>
          {t("analytics.sensorHealthDescription", {
            errors: erroringSensorCount,
            alerts: alertingSensorCount,
            total,
            stations: onlineHubCount,
          })}
        </Text>
        {offlineHubCount > 0 && (
          <Text fontSize="$3" color="$white10">
            {t("analytics.sensorHealthOffline", {
              count: offlineHubCount,
              sensors: sensorsBehindOfflineHubs,
            })}
          </Text>
        )}
        <YStack gap="$2">
          <HealthBar
            label={t("analytics.sensorHealthActive")}
            count={activeSensorCount}
            total={total}
            color="$successGreen"
          />
          <HealthBar
            label={t("analytics.sensorHealthAlerting")}
            count={alertingSensorCount}
            total={total}
            color="$warning"
          />
          <HealthBar
            label={t("analytics.sensorHealthError")}
            count={erroringSensorCount}
            total={total}
            color="$statusCritical"
          />
        </YStack>
      </YStack>
    </BorderedCard>
  );
};

export const SensorDistributionCard = ({
  distribution,
  totalSensors,
}: Pick<FleetAnalytics, "distribution" | "totalSensors">) => {
  const { translateSensorType } = useSensorType();
  const { t } = useTranslation();

  const chartBackgroundColor = getTokenValue(
    "$darkBackgroundGradient" as Token,
    "color",
  );

  return (
    <BorderedCard title={t("analytics.sensorDistribution")}>
      <XStack
        paddingVertical="$7"
        gap="$8"
        alignItems="center"
        justifyContent="center"
        $xs={{ flexWrap: "wrap", gap: "$5" }}
      >
        <View
          width={200}
          height={200}
          alignItems="center"
          justifyContent="center"
        >
          <PieChart
            data={distribution.map((item) => ({
              value: item.count,
              color: item.color,
              text: translateSensorType(item.type),
            }))}
            donut
            innerRadius={60}
            radius={100}
            innerCircleColor={chartBackgroundColor}
            centerLabelComponent={() => (
              <Text fontSize="$10" fontWeight="bold">
                {totalSensors}
              </Text>
            )}
          />
        </View>
        <YStack gap="$3" alignSelf="center">
          {distribution.map((item) => (
            <XStack
              key={item.type}
              alignItems="center"
              justifyContent="space-between"
              gap="$2"
            >
              <XStack alignItems="center" gap="$2">
                <View
                  width={8}
                  height={8}
                  borderRadius={5}
                  backgroundColor={item.color}
                />
                <Text maxWidth={120}>{translateSensorType(item.type)}</Text>
              </XStack>
              <Text
                backgroundColor="$transparentBorder"
                paddingHorizontal={5}
                paddingVertical={1}
                borderRadius={5}
              >
                {t("analytics.sensorDistributionCount", { count: item.count })}
              </Text>
            </XStack>
          ))}
        </YStack>
      </XStack>
    </BorderedCard>
  );
};
